import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageCropperProps {
  imageUrl: string;
  onCrop: (cropArea: CropArea) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageUrl,
  onCrop,
  onCancel,
  loading = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [displayDimensions, setDisplayDimensions] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  // Crop-Bereich in Bild-Koordinaten (nicht Display-Koordinaten)
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });

  // Drag-State
  const [isDragging, setIsDragging] = useState<'move' | 'nw' | 'ne' | 'sw' | 'se' | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialCrop, setInitialCrop] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });

  // Initialisiere Crop-Bereich wenn Bild geladen ist
  useEffect(() => {
    if (imageLoaded && imageDimensions.width > 0 && imageDimensions.height > 0) {
      // Standard: 80% des Bildes zentriert
      const margin = 0.1;
      setCropArea({
        x: Math.round(imageDimensions.width * margin),
        y: Math.round(imageDimensions.height * margin),
        width: Math.round(imageDimensions.width * (1 - 2 * margin)),
        height: Math.round(imageDimensions.height * (1 - 2 * margin)),
      });
    }
  }, [imageLoaded, imageDimensions]);

  // Berechne Display-Dimensionen
  useEffect(() => {
    if (!containerRef.current || !imageLoaded) return;

    const updateDimensions = () => {
      const container = containerRef.current;
      if (!container) return;

      const maxWidth = container.clientWidth - 40;
      const maxHeight = container.clientHeight - 40;

      const widthRatio = maxWidth / imageDimensions.width;
      const heightRatio = maxHeight / imageDimensions.height;
      const newScale = Math.min(widthRatio, heightRatio, 1);

      setScale(newScale);
      setDisplayDimensions({
        width: imageDimensions.width * newScale,
        height: imageDimensions.height * newScale,
      });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [imageLoaded, imageDimensions]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    setImageLoaded(true);
  };

  // Konvertiere Bild-Koordinaten zu Display-Koordinaten
  const toDisplayCoords = useCallback((area: CropArea) => ({
    x: area.x * scale,
    y: area.y * scale,
    width: area.width * scale,
    height: area.height * scale,
  }), [scale]);

  // Konvertiere Display-Koordinaten zu Bild-Koordinaten
  const toImageCoords = useCallback((x: number, y: number) => ({
    x: x / scale,
    y: y / scale,
  }), [scale]);

  const handleMouseDown = (e: React.MouseEvent, type: 'move' | 'nw' | 'ne' | 'sw' | 'se') => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(type);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialCrop({ ...cropArea });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = (e.clientX - dragStart.x) / scale;
    const deltaY = (e.clientY - dragStart.y) / scale;

    let newCrop = { ...initialCrop };
    const minSize = 50; // Minimale Größe in Bildpixeln

    if (isDragging === 'move') {
      newCrop.x = Math.max(0, Math.min(imageDimensions.width - newCrop.width, initialCrop.x + deltaX));
      newCrop.y = Math.max(0, Math.min(imageDimensions.height - newCrop.height, initialCrop.y + deltaY));
    } else if (isDragging === 'nw') {
      // Obere linke Ecke
      const maxDeltaX = initialCrop.width - minSize;
      const maxDeltaY = initialCrop.height - minSize;
      const clampedDeltaX = Math.max(-initialCrop.x, Math.min(maxDeltaX, deltaX));
      const clampedDeltaY = Math.max(-initialCrop.y, Math.min(maxDeltaY, deltaY));
      newCrop.x = initialCrop.x + clampedDeltaX;
      newCrop.y = initialCrop.y + clampedDeltaY;
      newCrop.width = initialCrop.width - clampedDeltaX;
      newCrop.height = initialCrop.height - clampedDeltaY;
    } else if (isDragging === 'ne') {
      // Obere rechte Ecke
      const maxDeltaY = initialCrop.height - minSize;
      const clampedDeltaY = Math.max(-initialCrop.y, Math.min(maxDeltaY, deltaY));
      newCrop.y = initialCrop.y + clampedDeltaY;
      newCrop.width = Math.max(minSize, Math.min(imageDimensions.width - initialCrop.x, initialCrop.width + deltaX));
      newCrop.height = initialCrop.height - clampedDeltaY;
    } else if (isDragging === 'sw') {
      // Untere linke Ecke
      const maxDeltaX = initialCrop.width - minSize;
      const clampedDeltaX = Math.max(-initialCrop.x, Math.min(maxDeltaX, deltaX));
      newCrop.x = initialCrop.x + clampedDeltaX;
      newCrop.width = initialCrop.width - clampedDeltaX;
      newCrop.height = Math.max(minSize, Math.min(imageDimensions.height - initialCrop.y, initialCrop.height + deltaY));
    } else if (isDragging === 'se') {
      // Untere rechte Ecke
      newCrop.width = Math.max(minSize, Math.min(imageDimensions.width - initialCrop.x, initialCrop.width + deltaX));
      newCrop.height = Math.max(minSize, Math.min(imageDimensions.height - initialCrop.y, initialCrop.height + deltaY));
    }

    setCropArea(newCrop);
  }, [isDragging, dragStart, initialCrop, scale, imageDimensions]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch-Events für Mobile
  const handleTouchStart = (e: React.TouchEvent, type: 'move' | 'nw' | 'ne' | 'sw' | 'se') => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    setIsDragging(type);
    setDragStart({ x: touch.clientX, y: touch.clientY });
    setInitialCrop({ ...cropArea });
  };

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || e.touches.length !== 1) return;
      e.preventDefault();
      const touch = e.touches[0];
      const syntheticEvent = { clientX: touch.clientX, clientY: touch.clientY } as MouseEvent;
      handleMouseMove(syntheticEvent);
    };

    const handleTouchEnd = () => {
      setIsDragging(null);
    };

    if (isDragging) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      return () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove]);

  const handleCrop = () => {
    // Runde auf ganze Pixel
    const finalCrop: CropArea = {
      x: Math.round(cropArea.x),
      y: Math.round(cropArea.y),
      width: Math.round(cropArea.width),
      height: Math.round(cropArea.height),
    };
    onCrop(finalCrop);
  };

  const handleReset = () => {
    if (imageDimensions.width > 0 && imageDimensions.height > 0) {
      const margin = 0.1;
      setCropArea({
        x: Math.round(imageDimensions.width * margin),
        y: Math.round(imageDimensions.height * margin),
        width: Math.round(imageDimensions.width * (1 - 2 * margin)),
        height: Math.round(imageDimensions.height * (1 - 2 * margin)),
      });
    }
  };

  const displayCrop = toDisplayCoords(cropArea);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col z-50">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Bild zuschneiden
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Zurücksetzen"
            >
              <ArrowPathIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={onCancel}
              disabled={loading}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Abbrechen"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Crop-Bereich */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center overflow-hidden p-4"
      >
        {!imageLoaded && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-400">Lade Bild...</p>
          </div>
        )}

        <div
          className="relative"
          style={{
            width: displayDimensions.width,
            height: displayDimensions.height,
            display: imageLoaded ? 'block' : 'none',
          }}
        >
          {/* Bild */}
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Crop"
            onLoad={handleImageLoad}
            className="absolute inset-0 w-full h-full object-contain select-none"
            draggable={false}
          />

          {/* Overlay für nicht-ausgewählten Bereich */}
          {imageLoaded && (
            <>
              {/* Obere Abdeckung */}
              <div
                className="absolute bg-black bg-opacity-50 pointer-events-none"
                style={{
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: displayCrop.y,
                }}
              />
              {/* Untere Abdeckung */}
              <div
                className="absolute bg-black bg-opacity-50 pointer-events-none"
                style={{
                  left: 0,
                  top: displayCrop.y + displayCrop.height,
                  width: '100%',
                  height: displayDimensions.height - displayCrop.y - displayCrop.height,
                }}
              />
              {/* Linke Abdeckung */}
              <div
                className="absolute bg-black bg-opacity-50 pointer-events-none"
                style={{
                  left: 0,
                  top: displayCrop.y,
                  width: displayCrop.x,
                  height: displayCrop.height,
                }}
              />
              {/* Rechte Abdeckung */}
              <div
                className="absolute bg-black bg-opacity-50 pointer-events-none"
                style={{
                  left: displayCrop.x + displayCrop.width,
                  top: displayCrop.y,
                  width: displayDimensions.width - displayCrop.x - displayCrop.width,
                  height: displayCrop.height,
                }}
              />

              {/* Crop-Box */}
              <div
                className="absolute border-2 border-white cursor-move"
                style={{
                  left: displayCrop.x,
                  top: displayCrop.y,
                  width: displayCrop.width,
                  height: displayCrop.height,
                }}
                onMouseDown={(e) => handleMouseDown(e, 'move')}
                onTouchStart={(e) => handleTouchStart(e, 'move')}
              >
                {/* Gitterlinien */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white bg-opacity-50" />
                  <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white bg-opacity-50" />
                  <div className="absolute top-1/3 left-0 right-0 h-px bg-white bg-opacity-50" />
                  <div className="absolute top-2/3 left-0 right-0 h-px bg-white bg-opacity-50" />
                </div>

                {/* Ecken zum Ziehen */}
                {/* Obere linke Ecke */}
                <div
                  className="absolute -left-2 -top-2 w-4 h-4 bg-white rounded-sm cursor-nw-resize shadow-lg"
                  onMouseDown={(e) => handleMouseDown(e, 'nw')}
                  onTouchStart={(e) => handleTouchStart(e, 'nw')}
                />
                {/* Obere rechte Ecke */}
                <div
                  className="absolute -right-2 -top-2 w-4 h-4 bg-white rounded-sm cursor-ne-resize shadow-lg"
                  onMouseDown={(e) => handleMouseDown(e, 'ne')}
                  onTouchStart={(e) => handleTouchStart(e, 'ne')}
                />
                {/* Untere linke Ecke */}
                <div
                  className="absolute -left-2 -bottom-2 w-4 h-4 bg-white rounded-sm cursor-sw-resize shadow-lg"
                  onMouseDown={(e) => handleMouseDown(e, 'sw')}
                  onTouchStart={(e) => handleTouchStart(e, 'sw')}
                />
                {/* Untere rechte Ecke */}
                <div
                  className="absolute -right-2 -bottom-2 w-4 h-4 bg-white rounded-sm cursor-se-resize shadow-lg"
                  onMouseDown={(e) => handleMouseDown(e, 'se')}
                  onTouchStart={(e) => handleTouchStart(e, 'se')}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer mit Buttons */}
      <div className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {imageLoaded && (
              <span>
                Auswahl: {Math.round(cropArea.width)} x {Math.round(cropArea.height)} px
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCrop}
              disabled={loading || !imageLoaded}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Speichern...
                </>
              ) : (
                <>
                  <CheckIcon className="h-5 w-5" />
                  Zuschneiden
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
